import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { ApplicationsService } from '../../../core/data/applications.service';
import { FilesService } from '../../../core/data/files.service';
import { Application } from '../../../core/models/application.model';
import { Version } from '../../../core/models/version.model';
import { UserSettingsService } from '../../../core/utils/user-settings.service';

@Component({
  selector: 'idl-application-edit',
  templateUrl: './application-edit.component.html',
  styleUrls: ['./application-edit.component.scss']
})
export class ApplicationEditComponent implements OnInit, OnChanges, OnDestroy {
  private unsubscribe$ = new Subject<void>();

  @Input() addApplication = false;
  @Input() application: Application;
  latestVersion: Version;
  submitted = false;
  favourite = false;

  @Output() saved: EventEmitter<any> = new EventEmitter();
  @Input() saveEvent: Observable<void>;

  ipPattern =
    '(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)';

  appForm = this._fb.group({
    name: ['', Validators.required],
    applicationGuid: [''],
    description: [''],
    comments: [''],
    licenseKeyName: [''],
    password: [''],
    publicKey: [''],
    noLicensedDownload: [false],
    hidden: [false],
    enableDemos: [false],
    demoPassword: [''],
    demoIPFilter: ['', Validators.pattern(this.ipPattern)],
    maxDemoDays: [0]
  });

  constructor(
    private _applicationsService: ApplicationsService,
    private _filesService: FilesService,
    private _fb: UntypedFormBuilder,
    private _snackBar: MatSnackBar,
    private _userSettingsService: UserSettingsService
  ) {
    this.latestVersion = new Version();
    this.application = new Application();
    this.application.applicationId = 0;
  }

  ngOnInit() {
    this.saveEvent
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => this.saveApp());

    if (!this.addApplication) {
      this._userSettingsService
        .getSettings()
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(settings => {
          if (settings.appFavourites) {
            this.favourite = settings.appFavourites.includes(
              this.application.applicationId
            );
          }
        });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.application) {
      this.appForm.patchValue({
        name: this.application.name,
        applicationGuid: this.application.applicationGuid,
        description: this.application.description,
        comments: this.application.comments,
        licenseKeyName: this.application.licenseKeyName,
        password: this.application.password,
        publicKey: this.application.publicKey,
        noLicensedDownload: this.application.noLicenseDownload,
        hidden: this.application.hidden,
        enableDemos: this.application.enableDemos,
        demoPassword: this.application.demoPassword,
        demoIPFilter: this.application.demoIPFilter,
        maxDemoDays: this.application.maxDemoDays
      });
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  toggleFavourite() {
    this._userSettingsService.toggleAppFavourite(
      this.application.applicationId
    );
  }

  saveApp() {
    this.submitted = true;

    this.application.name = this.appForm.value.name;
    this.application.description = this.appForm.value.description;
    this.application.comments = this.appForm.value.comments;
    this.application.licenseKeyName = this.appForm.value.licenseKeyName;
    this.application.noLicenseDownload = this.appForm.value.noLicensedDownload;
    this.application.hidden = this.appForm.value.hidden;
    this.application.enableDemos = this.appForm.value.enableDemos;
    this.application.demoPassword = this.appForm.value.demoPassword;
    this.application.demoIPFilter = this.appForm.value.demoIPFilter;
    this.application.maxDemoDays = this.appForm.value.maxDemoDays;

    this._applicationsService
      .edit(this.application)
      .pipe(take(1))
      .subscribe(app => {
        this._snackBar.open('Application successfully saved.', 'Saved', {
          duration: 5000,
          verticalPosition: 'top'
        });

        if (this.addApplication) {
          this.saved.emit(app);
        }
      });

    this.submitted = false;
  }

  downloadLatest() {
    if (this.latestVersion.fileId > 0) {
      this._filesService
        .download(this.latestVersion.fileId)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(event => {
          if (event.blob) {
            // It is necessary to create a new blob object with mime-type explicitly set
            // otherwise only Chrome works like it should
            const newBlob = new Blob([event.blob], { type: event.type });

            // IE doesn't allow using a blob object directly as link href
            // instead it is necessary to use msSaveOrOpenBlob
            if (window.navigator && window.navigator.msSaveOrOpenBlob) {
              window.navigator.msSaveOrOpenBlob(
                newBlob,
                this.latestVersion.name
              );
              return;
            }

            // For other browsers:
            // Create a link pointing to the ObjectURL containing the blob.
            const data = window.URL.createObjectURL(newBlob);

            const link = document.createElement('a');
            link.href = data;
            link.download = this.latestVersion.name;
            // this is necessary as link.click() does not work on the latest firefox
            link.dispatchEvent(
              new MouseEvent('click', {
                bubbles: true,
                cancelable: true,
                view: window
              })
            );

            setTimeout(function() {
              // For Firefox it is necessary to delay revoking the ObjectURL
              window.URL.revokeObjectURL(data);
              link.remove();
            }, 100);
          }
        });
    }
  }

  copyInput(inputElement) {
    inputElement.select();
    document.execCommand('copy');
    inputElement.setSelectionRange(0, 0);
  }
}
